import { type WritableDraft } from 'immer';
import { map, type Observable, of } from 'rxjs';

import { type CanvasElementFrom, type PartialPropsOf } from '@blaze-runtime/engine-domain';
import { applyPropsPatch } from '@blaze-runtime/engine-domain';

import { type EngineState } from '../../../+state/engine';
import {
  AbstractAiElementHandler,
  addEventWorkflowWithConnection,
  getElement,
  getInitialProps,
  upsertElement,
} from '../../../ai';
import { toHeightSettings, toWidthSettings } from '../../../ai/models/styles';

import { type ButtonContext, type ButtonResolvedContext } from './button.definition';

export class ButtonElementHandler extends AbstractAiElementHandler<'button'> {
  readonly type = 'button';

  prepare(context: ButtonContext): Observable<ButtonResolvedContext> {
    this.resolveElementIcon(context.element);

    const workflowContext = this.resolveWorkflowContext(context);

    return of(workflowContext ? { ...context, workflowContext } : context);
  }

  process(context: ButtonResolvedContext, draft: WritableDraft<EngineState>): void {
    let props: PartialPropsOf<'button'> = getInitialProps<'button'>(draft, context.element.id, {
      content: {
        button: {},
      },
      styleConfig: {
        styleName: context.element.styleName,
      },
    });

    props = applyPropsPatch(props, {
      content: {
        button: {
          label: context.element.label ?? '',
        },
      },
    });

    if (context.element.iconName) {
      props = applyPropsPatch(props, {
        content: {
          button: {
            icon: context.element.iconName,
          },
        },
      });
    }

    if (context.element.showLoadersForLongActions) {
      props = applyPropsPatch(props, {
        content: {
          button: {
            showLoader: context.element.showLoadersForLongActions,
          },
        },
      });
    }

    const submitsForm = context.element.specialPurpose === 'SubmitsForm';

    if (context.workflowContext && !submitsForm) {
      props = applyPropsPatch(props, {
        content: {
          action: {
            workflowId: context.workflowContext.workflow.id,
          },
        },
      });
    }

    const result = upsertElement(draft, context, props);

    if (context.workflowContext && !submitsForm) {
      const { workflow } = context.workflowContext;
      const { hasUpdates } = addEventWorkflowWithConnection(draft, context, workflow, 'click');

      context.workflowsToFinalize = [{ workflow, forceUpdate: hasUpdates }];
    }

    const element = getElement(draft, result.elementId, 'button');
    element.width = toWidthSettings(context.element.width ?? { $type: 'fitContent' });
    element.height = toHeightSettings(context.element.height ?? { $type: 'fitContent' });
  }

  finalize(
    _element: CanvasElementFrom<'button'>,
    context: ButtonResolvedContext,
  ): Observable<void> {
    return this.aiWorkflowGenerationService
      .handleAiGeneratedWorkflows(context.workflowsToFinalize ?? [])
      .pipe(map(() => undefined));
  }
}
